import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import Nav from '../components/Nav';
import Footer from '../components/Footer';

type ContactRow = {
  label: string;
  email: string;
};

const contacts: ContactRow[] = [
  { label: 'Support', email: 'support@dinksocial.com' },
  { label: 'General Inquiries', email: 'hello@dinksocial.com' },
  { label: 'Partnership Opportunities', email: 'partnerships@dinksocial.com' },
  { label: 'Press', email: 'press@dinksocial.com' },
];

const ContactPage: React.FC = () => {
  return (
    <div className="font-display text-ink bg-white min-h-screen">
      <Helmet>
        <title>Contact | DINK Social</title>
        <meta name="description" content="Get in touch with the DINK Social team. Reach out for support, partnerships, press inquiries, or general questions about the childfree couples community." />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Contact | DINK Social" />
        <meta property="og:description" content="Get in touch with the DINK Social team for support, partnerships, or press inquiries." />
        <meta property="og:url" content="https://dinksocial.com/contact" />
        <meta property="og:site_name" content="DINK Social" />
        <meta property="og:image" content="https://dinksocial.com/og-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Contact | DINK Social" />
        <meta name="twitter:description" content="Get in touch with the DINK Social team for support, partnerships, or press inquiries." />
        <meta name="twitter:image" content="https://dinksocial.com/og-image.jpg" />
        <link rel="canonical" href="https://dinksocial.com/contact" />
      </Helmet>

      <Nav />

      {/* ─── HEADING ───────────────────────────────────────── */}
      <section className="pt-32 md:pt-40 pb-12 md:pb-16 px-6 md:px-10">
        <div className="max-w-3xl mx-auto text-center">
          <h1
            className="font-display font-medium text-ink text-[42px] md:text-[56px] lg:text-[64px] mb-5"
            style={{ lineHeight: 1.02, letterSpacing: '-0.02em' }}
          >
            Get in touch
          </h1>
          <p
            className="font-playfair italic font-medium text-ink/85 text-[20px] md:text-[24px]"
            style={{ lineHeight: 1.3, letterSpacing: '-0.015em' }}
          >
            We'd love to hear from you
          </p>
        </div>
      </section>

      {/* ─── CONTACT LIST ──────────────────────────────────── */}
      <section className="pb-20 md:pb-24 px-6 md:px-10">
        <div className="max-w-2xl mx-auto">
          <ul className="divide-y divide-ink/10 border-y border-ink/10">
            {contacts.map((c) => (
              <li
                key={c.email}
                className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 py-6"
              >
                <span className="text-[11px] uppercase tracking-[0.22em] font-medium text-ink/60">
                  {c.label}
                </span>
                <a
                  href={`mailto:${c.email}`}
                  className="text-[17px] md:text-[18px] font-medium text-primary hover:text-primary-dark hover:underline transition-colors"
                >
                  {c.email}
                </a>
              </li>
            ))}
          </ul>
          <p
            className="text-[15px] md:text-[16px] text-ink/70 mt-8 text-center"
            style={{ lineHeight: 1.55 }}
          >
            {'Interested in partnering with us? '}
            <Link
              to="/partners"
              className="text-primary font-medium hover:text-primary-dark hover:underline transition-colors"
            >
              See how we work with partners
            </Link>
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
